import { useEffect, useRef } from 'react'
import { useSearchParams } from './use-search-params'

interface UseUrlEnumStateOptions<T extends string> {
  /** URL query parameter name to bind to. */
  paramName: string
  /** Value used when the param is missing or invalid. Also the value that *removes* the param from the URL. */
  defaultValue: T
  /** Whitelist of accepted values. Anything outside this list falls back to `defaultValue`. */
  values: readonly T[]
}

function readValue<T extends string>(
  searchParams: URLSearchParams,
  paramName: string,
  values: readonly T[],
  defaultValue: T
): T {
  const raw = searchParams.get(paramName)
  return values.includes(raw as T) ? (raw as T) : defaultValue
}

/**
 * Two-way binds an enum-like string state to a URL query parameter so the browser
 * Back/Forward buttons act on it.
 *
 * - Initial value is read from the URL.
 * - Calling the returned setter pushes a new history entry, so Back returns to the previous value.
 * - On `popstate`, state re-syncs from the URL without writing another entry.
 * - When the value equals `defaultValue`, the param is removed to keep URLs clean.
 */
export function useUrlEnumState<T extends string>({
  paramName,
  defaultValue,
  values,
}: UseUrlEnumStateOptions<T>): [T, (next: T) => void] {
  const [searchParams, setSearchParams] = useSearchParams()
  const value = readValue(searchParams, paramName, values, defaultValue)
  const isFirstRender = useRef(true)

  useEffect(() => {
    if (!isFirstRender.current) return
    isFirstRender.current = false

    const current = searchParams.get(paramName)
    const isInvalid = current !== null && !values.includes(current as T)
    const isDefault = current === defaultValue
    if (isInvalid || isDefault) {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.delete(paramName)
          return next
        },
        { replace: true }
      )
    }
  }, [paramName, defaultValue, values, searchParams, setSearchParams])

  const setValue = (next: T) => {
    setSearchParams((prev) => {
      const sp = new URLSearchParams(prev)
      if (next === defaultValue) sp.delete(paramName)
      else sp.set(paramName, next)
      return sp
    })
  }

  return [value, setValue]
}
